import type {
  SignageCecControlCapabilityRecord,
  SignageCecControlStatus,
  SignageDeviceRecord,
  SignageDisplayCecControlsRecord
} from 'noddi-async';
import { parseSignageCecControlStatus } from 'noddi-async';
import { NoddiDateFormats, noddiFormatDate } from 'noddi-util';
import { signageProbeEdidIdentity } from './signageDisplayProbe';

type DeviceNameFields = Pick<SignageDeviceRecord, 'name' | 'hostname' | 'fleetDeviceId' | 'pairingCode'>;

type ContentFields = Pick<
  SignageDeviceRecord,
  'contentType' | 'playlistName' | 'dashboardName' | 'contentPresetName' | 'assignedUrl'
>;

/** Prefer staff name, then host / fleet id, then pairing code. */
export const signageDeviceDisplayName = (device: DeviceNameFields, fallback = '(unnamed)'): string => {
  const name = device.name?.trim();
  if (name) {
    return name;
  }
  const host = device.hostname?.trim();
  if (host) {
    return host;
  }
  const fleet = device.fleetDeviceId?.trim();
  if (fleet) {
    return fleet;
  }
  return device.pairingCode?.trim() || fallback;
};

export const signageEmpty = (value: string | null | undefined): string => {
  const trimmed = value?.trim();
  return trimmed ? trimmed : '—';
};

export const signageContentLabel = (data: ContentFields): string => {
  switch (data.contentType) {
    case 'playlist':
      return data.playlistName ? `Playlist · ${data.playlistName}` : 'Playlist';
    case 'dashboard':
      return data.dashboardName ? `Dashboard · ${data.dashboardName}` : 'Dashboard';
    case 'url':
      return data.contentPresetName ? `URL · ${data.contentPresetName}` : data.assignedUrl || 'URL';
    default:
      return 'No content';
  }
};

export const signageFormatDateTime = (value: string | null | undefined): string =>
  value ? noddiFormatDate(value, NoddiDateFormats.DATE_TIME_SECONDS) : '—';

export const signageDeviceOptionLabel = (
  device: Pick<SignageDeviceRecord, 'id' | 'name' | 'fleetDeviceId' | 'location'>
): string => `#${device.id} ${device.name || device.fleetDeviceId || device.location || 'device'}`;

/** Prefer viewport (what Chromium paints), else HDMI mode from OS heartbeat. */
export const signageDisplayResolution = (
  device: Pick<
    SignageDeviceRecord,
    'displayWidth' | 'displayHeight' | 'viewportWidth' | 'viewportHeight' | 'displayRefreshHz'
  >
): string => {
  const width = device.viewportWidth ?? device.displayWidth;
  const height = device.viewportHeight ?? device.displayHeight;
  if (width == null || height == null || width <= 0 || height <= 0) {
    return '—';
  }
  const hz = device.displayRefreshHz;
  return hz != null && hz > 0 ? `${width}×${height} @ ${hz}Hz` : `${width}×${height}`;
};

type DisplayInfoFields = Pick<SignageDeviceRecord, 'displayInfo'>;

/** TV model label from EDID / CEC OSD name when the RPi has probed HDMI. */
export const signageTvLabel = (device: DisplayInfoFields): string => {
  const info = device.displayInfo;
  if (!info) {
    return '—';
  }
  const identity = signageProbeEdidIdentity(info.edid);
  return identity !== '—' ? identity : signageEmpty(info.cec?.tvOsdName);
};

/** HDMI link status: connected / disconnected / unknown. */
export const signageDisplayLinkLabel = (device: DisplayInfoFields): string => {
  const link = device.displayInfo?.link?.trim().toLowerCase();
  if (!link) {
    return '—';
  }
  if (link === 'connected') {
    return 'Connected';
  }
  if (link === 'disconnected') {
    return 'Disconnected';
  }
  return link;
};

/**
 * Whether an HDMI panel is attached per last OS probe.
 * - true: link connected
 * - false: link explicitly disconnected
 * - null: no probe yet / unknown
 */
export const signageDisplayConnected = (device: DisplayInfoFields): boolean | null => {
  const link = device.displayInfo?.link?.trim().toLowerCase();
  if (!link) {
    return null;
  }
  if (link === 'connected') {
    return true;
  }
  if (link === 'disconnected') {
    return false;
  }
  return null;
};

/** CEC-reported TV power (on / standby / unknown). */
export const signageTvPowerLabel = (device: DisplayInfoFields): string => {
  const power = device.displayInfo?.cec?.tvPower?.trim().toLowerCase();
  if (!power) {
    return '—';
  }
  if (power === 'on') {
    return 'On';
  }
  if (power === 'standby' || power === 'off') {
    return 'Standby';
  }
  return power;
};

/** Whether CEC was observed available on last probe (best-effort). */
export const signageCecAvailable = (device: DisplayInfoFields): boolean | null => {
  const available = device.displayInfo?.cec?.available;
  if (typeof available === 'boolean') {
    return available;
  }
  return null;
};

export type SignageCecControlName = keyof SignageDisplayCecControlsRecord;

const SIGNAGE_CEC_CONTROL_NAMES: SignageCecControlName[] = ['power', 'wake', 'standby', 'volume', 'mute'];

type SignageCecControlSummaryItem = {
  name: SignageCecControlName;
  status: SignageCecControlStatus;
};

const CEC_CONTROL_LABEL: Record<SignageCecControlName, string> = {
  power: 'Power',
  wake: 'Wake',
  standby: 'Standby',
  volume: 'Volume',
  mute: 'Mute'
};

/** Long label + compact token per capability status — one place to extend. */
const CEC_STATUS_TEXT: Record<SignageCecControlStatus, { label: string; short: string }> = {
  supported: { label: 'Supported', short: 'ok' },
  likely: { label: 'Likely', short: '~' },
  unknown: { label: 'Unverified', short: '?' },
  unsupported: { label: 'Unsupported', short: 'no' },
  unavailable: { label: 'Unavailable', short: 'no' },
  blocked: { label: 'Blocked', short: 'block' }
};

/** Human control name for tooltips / labels. */
export const signageCecControlNameLabel = (name: SignageCecControlName): string => CEC_CONTROL_LABEL[name];

/** Human label for a capability status (badges / tooltips). */
export const signageCecStatusLabel = (status: SignageCecControlStatus | null | undefined): string =>
  (status && CEC_STATUS_TEXT[status]?.label) || '—';

/** Compact text token used in plain-string summaries. */
export const signageCecStatusShort = (status: SignageCecControlStatus): string => CEC_STATUS_TEXT[status].short;

/** Read one control capability entry from last display probe. */
export const signageCecControl = (
  device: DisplayInfoFields,
  name: SignageCecControlName
): SignageCecControlCapabilityRecord | null => {
  const entry = device.displayInfo?.cec?.controls?.[name];
  return entry ?? null;
};

/** Probe reason when the panel never ACKs on the CEC bus. */
const CEC_NO_TV_RESPONSE_REASON = 'no_tv_response';

const isNoTvResponseReason = (reason: string | null | undefined): boolean =>
  (reason?.trim().toLowerCase() ?? '') === CEC_NO_TV_RESPONSE_REASON;

/**
 * True when the last probe indicates the panel is silent on CEC
 * (no logical-address 0 / power query / OSD), even if a stale learned
 * "supported" still sits on power from an earlier tool-level wake.
 */
export const signageCecBusSilent = (device: DisplayInfoFields): boolean => {
  if (device.displayInfo?.cec?.tvPresent === false) {
    return true;
  }
  // Passive derive_controls sets both wake + standby to no_tv_response together.
  return (
    isNoTvResponseReason(signageCecControl(device, 'wake')?.reason) &&
    isNoTvResponseReason(signageCecControl(device, 'standby')?.reason)
  );
};

/**
 * Effective capability status for chips / button enablement.
 *
 * Probe learning can leave power as `supported` (e.g. reason `wake_ok`) after
 * cec-client returned 0, while the live scan reports `no_tv_response` on
 * wake/standby. Demote optimistic statuses so UI shows Unsupported when the
 * panel is not actually on the CEC bus (common on cheap monitors / CEC off).
 */
export const resolveSignageCecControlStatus = (
  device: DisplayInfoFields,
  name: SignageCecControlName
): SignageCecControlStatus | null => {
  const entry = signageCecControl(device, name);
  const raw = parseSignageCecControlStatus(entry?.status);
  if (!raw) {
    return null;
  }

  // Policy / hard agent statuses always win.
  if (raw === 'blocked' || raw === 'unavailable' || raw === 'unsupported') {
    return raw;
  }

  const reason = entry?.reason?.trim().toLowerCase() ?? '';

  // Trust a verified power query — TV is answering CEC.
  if (name === 'power' && reason === 'pow_query_ok') {
    return raw;
  }

  // This control's own probe said no TV response.
  if (isNoTvResponseReason(reason)) {
    return 'unsupported';
  }

  // Bus silent: demote optimistic statuses on every control.
  if (signageCecBusSilent(device) && (raw === 'supported' || raw === 'likely' || raw === 'unknown')) {
    return 'unsupported';
  }

  return raw;
};

/**
 * Whether the partner UI should enable a TV control.
 * - supported / likely → enabled
 * - unknown → enabled (optimistic; show soft warning at bar level)
 * - blocked / unavailable / unsupported → disabled
 * - no probe yet → enabled (optimistic until first heartbeat)
 */
export const signageCecControlEnabled = (device: DisplayInfoFields, name: SignageCecControlName): boolean => {
  const status = resolveSignageCecControlStatus(device, name);
  if (!status) {
    return true;
  }
  return status === 'supported' || status === 'likely' || status === 'unknown';
};

/** Short human label for a control status (badges / tooltips). */
export const signageCecControlStatusLabel = (device: DisplayInfoFields, name: SignageCecControlName): string =>
  signageCecStatusLabel(resolveSignageCecControlStatus(device, name));

/** Structured per-control capability list for UI chips / string summaries. */
export const signageCecControlSummaryItems = (device: DisplayInfoFields): SignageCecControlSummaryItem[] | null => {
  const controls = device.displayInfo?.cec?.controls;
  if (!controls) {
    return null;
  }
  return SIGNAGE_CEC_CONTROL_NAMES.map((name) => ({
    name,
    status: resolveSignageCecControlStatus(device, name) ?? 'unknown'
  }));
};
